'use client';

import type { LocationsPanelLocation } from '../../../../api/nav-panel-data';
import { useNow } from '../../../../utilities/hooks/use-now';
import { resolveOpeningStatus } from '../../../../utilities/opening-status';

/**
 * A location's details as a label/value list: "Visit" (the address) and
 * "Today" ("Open now · until 6 pm"). The header is cached, so today's hours
 * are worked out in the browser: the "Today" row is left out of the served
 * HTML, and wherever the schedule is too partial to make a confident claim.
 * The list itself is drawn only when it has a row, so a location with hours
 * and no address renders nothing until the browser knows the day, rather
 * than an empty `<dl>`.
 */
export const VisitDetails: React.FC<{
  address: LocationsPanelLocation['address'];
  hours: LocationsPanelLocation['hours'];
  labelClassName: string;
}> = ({ address, hours, labelClassName }) => {
  const now = useNow();
  const status = now === null || hours === null ? null : resolveOpeningStatus({ hours: hours.week, timeZone: hours.timeZone, now });
  if (address === null && status === null) { return null; }

  return (
    <dl className="grid grid-cols-label-value items-start gap-x-3 gap-y-2">
      {address === null ? null : (
        <>
          <dt className={labelClassName}>Visit</dt>
          <dd className="col-start-2">{address}</dd>
        </>
      )}
      {status === null ? null : (
        <>
          <dt className={labelClassName}>Today</dt>
          <dd className="col-start-2 tabular-nums">{status.text}</dd>
        </>
      )}
    </dl>
  );
};
